import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { IntakeAssistantAPI } from "../api/intakeAssistantApi";

/**
 * One fetch of each document, shared by every card that cites it.
 *
 * Casey routinely suggests several answers from a single passport, and each card
 * renders a crop of the same PDF. Without a shared cache that is one download per
 * card of a file the client is on a phone connection with, and one more every
 * time a card re-renders.
 */

type DocumentState =
  | { status: "loading"; url: null }
  | { status: "ready"; url: string }
  | { status: "unavailable"; url: null };

const UNAVAILABLE: DocumentState = { status: "unavailable", url: null };

type CitationDocuments = {
  request: (attachmentId: string) => void;
  documents: Record<string, DocumentState>;
};

const CitationDocumentsContext = createContext<CitationDocuments | null>(null);

export function CitationDocumentsProvider({
  token,
  children
}: {
  token: string;
  children: React.ReactNode;
}) {
  const [documents, setDocuments] = useState<Record<string, DocumentState>>({});
  // Requests in flight, kept out of state because a second card asking for the
  // same document must not restart the fetch or re-render anything.
  const requested = useRef<Set<string>>(new Set());
  const objectUrls = useRef<string[]>([]);

  useEffect(
    () => () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.current = [];
    },
    []
  );

  const request = useCallback(
    (attachmentId: string) => {
      if (!token || !attachmentId || requested.current.has(attachmentId))
        return;
      requested.current.add(attachmentId);

      setDocuments((current) => ({
        ...current,
        [attachmentId]: { status: "loading", url: null }
      }));

      IntakeAssistantAPI.fetchCitationPdf(token, attachmentId)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          objectUrls.current.push(url);
          setDocuments((current) => ({
            ...current,
            [attachmentId]: { status: "ready", url }
          }));
        })
        .catch(() => {
          // Expected for anything extracted before the server kept a copy, so
          // this is a fallback rather than an error worth showing.
          setDocuments((current) => ({
            ...current,
            [attachmentId]: UNAVAILABLE
          }));
        });
    },
    [token]
  );

  const value = useMemo(() => ({ request, documents }), [request, documents]);

  return (
    <CitationDocumentsContext.Provider value={value}>
      {children}
    </CitationDocumentsContext.Provider>
  );
}

/**
 * The cited document, fetched on first use.
 *
 * Reports `unavailable` outside a provider as well as on failure, so a card
 * rendered without one falls back to quoting the line rather than breaking.
 */
export function useCitationDocument(
  attachmentId: string | null | undefined,
  enabled = true
): DocumentState {
  const context = useContext(CitationDocumentsContext);

  useEffect(() => {
    if (!enabled || !attachmentId || !context) return;
    context.request(attachmentId);
  }, [attachmentId, context, enabled]);

  if (!enabled || !attachmentId || !context) return UNAVAILABLE;
  return context.documents[attachmentId] ?? { status: "loading", url: null };
}
